import { T, Var } from 'gt-react';
import type { SuggestionCartStatus } from '@erp/rpc';
import { formatCurrency } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { bindingThreshold, cartShortfall } from '../components/cartShortfall';

/**
 * How the buy's vendor cart stands against its minimum right now, from the
 * live cart the suggestion detail carries. Same threshold rule as the belt
 * card's note and the detail view's chip (`cartShortfall`): the higher of MOV
 * and prepaid freight binds, MOV winning a tie. Renders nothing when the
 * vendor quotes neither threshold for this ship-to.
 */
export function CartMinimumStatus({ cart }: { cart: SuggestionCartStatus }) {
  const binding = bindingThreshold(cart.movThreshold, cart.freightThreshold);
  if (binding === null) return null;
  const shortfall = cartShortfall(cart);

  const name = cart.shipToWarehouseName;
  const total = formatCurrency(cart.cartTotal);
  const amount = formatCurrency(binding.amount);

  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        shortfall == null
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-amber-100 text-amber-700',
      )}
    >
      {shortfall != null && binding.constraint === 'mov' && (
        <T context="suggestion detail: the buy's vendor cart total, the vendor's minimum order value, and how far short of it the cart still is">
          <Var>{name}</Var> cart <Var>{total}</Var> of the <Var>{amount}</Var>{' '}
          minimum order value · <Var>{formatCurrency(shortfall.gap)}</Var> short
        </T>
      )}
      {shortfall != null && binding.constraint === 'freight' && (
        <T context="suggestion detail: the buy's vendor cart total, the vendor's prepaid-freight minimum, and how far short of it the cart still is">
          <Var>{name}</Var> cart <Var>{total}</Var> of the <Var>{amount}</Var>{' '}
          prepaid-freight minimum · <Var>{formatCurrency(shortfall.gap)}</Var>{' '}
          short
        </T>
      )}
      {shortfall == null && binding.constraint === 'mov' && (
        <T context="suggestion detail: the buy's vendor cart total has reached the vendor's minimum order value">
          <Var>{name}</Var> cart <Var>{total}</Var> · <Var>{amount}</Var>{' '}
          minimum order value met
        </T>
      )}
      {shortfall == null && binding.constraint === 'freight' && (
        <T context="suggestion detail: the buy's vendor cart total has reached the vendor's prepaid-freight minimum">
          <Var>{name}</Var> cart <Var>{total}</Var> · <Var>{amount}</Var>{' '}
          prepaid-freight minimum met
        </T>
      )}
    </span>
  );
}
